<template>
  <div class="date-range-selector">
    <div class="flex flex-wrap items-center gap-3">
      <SelectButton
        :modelValue="modelRange"
        :options="rangeSelectOptions"
        optionLabel="label"
        optionValue="value"
        :allowEmpty="false"
        @change="({ value }) => selectRange(value)"
      />

      <div v-if="modelRange === 'custom'" class="custom-range-inputs flex items-center gap-2 ml-4">
        <div class="input-group">
          <label for="input-date-range-from-date" class="text-xs text-gray-400 block mb-1 select-none">
            {{ $t('botEditor.analytics.dateRange.from') }}
          </label>
          <!-- :manualInput="false" on both pickers: PrimeVue 4.5.5 drops typed text in 24-hour mode
               (populateTime reads the missing AM/PM part and throws inside onInput), so the field is
               honestly picker-only until that upstream bug is fixed. -->
          <DatePicker
            inputId="input-date-range-from-date"
            :modelValue="customFromDate"
            showTime
            hourFormat="24"
            showIcon
            iconDisplay="input"
            :manualInput="false"
            @update:modelValue="updateCustomFrom"
          />
        </div>

        <span class="text-gray-500">—</span>

        <div class="input-group">
          <label for="input-date-range-to-date" class="text-xs text-gray-400 block mb-1 select-none">
            {{ $t('botEditor.analytics.dateRange.to') }}
          </label>
          <DatePicker
            inputId="input-date-range-to-date"
            :modelValue="customToDate"
            showTime
            hourFormat="24"
            showIcon
            iconDisplay="input"
            :manualInput="false"
            @update:modelValue="updateCustomTo"
          />
        </div>

        <Button
          id="btn-apply-date-range"
          severity="primary"
          :label="$t('botEditor.analytics.dateRange.applyButton')"
          :disabled="!modelCustomFrom || !modelCustomTo"
          class="mt-5"
          @click="applyCustomRange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
/**
 * AnalyticsDateRange.vue
 *
 * Preset range picker (today / week / month / quarter) plus a custom "From" / "To" range, where
 * each bound is a PrimeVue DatePicker with a 24h time picker. The custom bounds are exchanged
 * with the parent as ISO strings (`Date#toISOString()`, UTC). `update` fires when a preset is
 * picked or the custom range is applied.
 *
 * @example
 * <AnalyticsDateRange
 *   v-model:range="selectedRange"
 *   v-model:customFrom="customFrom"
 *   v-model:customTo="customTo"
 *   @update="fetchAnalytics"
 * />
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  range: {
    type: String,
    default: '1d'
  },
  customFrom: {
    type: String,
    default: null
  },
  customTo: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:range', 'update:customFrom', 'update:customTo', 'update']);

const { t } = useI18n();

const rangeValues = ['1d', '7d', '30d', '90d', 'custom'];

const modelRange = computed({
  get: () => props.range,
  set: (value) => emit('update:range', value)
});

const modelCustomFrom = computed({
  get: () => props.customFrom,
  set: (value) => emit('update:customFrom', value)
});

const modelCustomTo = computed({
  get: () => props.customTo,
  set: (value) => emit('update:customTo', value)
});

const rangeSelectOptions = computed(() => rangeValues.map(value => ({
  value,
  label: t(`botEditor.analytics.dateRange.${value}`)
})));

const toIsoString = (date) => (date ? date.toISOString() : null);

/**
 * Bridges one custom bound (an ISO string in the parent) and the Date its DatePicker holds.
 * Edits are truncated to the minute (seconds and ms zeroed), like the former date + HH + MM
 * inputs; values set by the parent (e.g. the defaults in selectRange) are shown as they are.
 * Every edit stores a fresh, minute-truncated Date, so the DatePicker always receives a new
 * modelValue even when the ISO string is unchanged.
 *
 * @param {import('vue').WritableComputedRef<string|null>} model - modelCustomFrom or modelCustomTo
 * @returns {{ date: import('vue').Ref<Date|null>, update: (value: Date|null) => void }}
 */
const useBoundDate = (model) => {
  const date = ref(null);

  watch(model, (iso) => {
    if (iso !== toIsoString(date.value)) {
      date.value = iso ? new Date(iso) : null;
    }
  }, { immediate: true });

  const update = (value) => {
    const minute = value ? new Date(value) : null;
    minute?.setSeconds(0, 0);
    date.value = minute;
    model.value = toIsoString(minute);
  };

  return { date, update };
};

const { date: customFromDate, update: updateCustomFrom } = useBoundDate(modelCustomFrom);
const { date: customToDate, update: updateCustomTo } = useBoundDate(modelCustomTo);

const selectRange = (value) => {
  modelRange.value = value;
  
  // Se seleziona custom e non ci sono date, inizializza con valori di default
  if (value === 'custom') {
    if (!modelCustomFrom.value) {
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      modelCustomFrom.value = yesterday.toISOString();
      modelCustomTo.value = now.toISOString();
    }
  } else {
    // Se non è custom, emetti subito l'evento di update
    emit('update');
  }
};

const applyCustomRange = () => {
  if (modelCustomFrom.value && modelCustomTo.value) {
    emit('update');
  }
};
</script>

<style scoped>

@reference "../../style.css";

.date-range-selector {
  @apply bg-gray-800/50 border border-gray-700 rounded-lg p-4;
}

.custom-range-inputs {
  @apply border-l border-gray-600 pl-4;
}

/* shrink-0: the DatePicker input is `width: 1%; flex: 1 1 auto`, so in a tight row it could
   collapse; the former fixed-width date/time inputs never shrank. */
.input-group {
  @apply flex flex-col shrink-0;
}

</style>
